import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Calendar2Solid,
  Check,
  DotsHorizontal,
  InputSearch,
  Menu,
  MenuItem,
  ModalPresetConfirm,
  Pause,
  Play,
  Popper,
  Trash,
  useModal,
  useSearchFilter,
} from '@dashboard/ui'
import {
  Badge,
  DateTimeValue,
  PageHeader,
  Table,
  TableCell,
  TableRow,
  TableRowSkeleton,
  ToastSeverity,
  useToast,
} from '@/ui'
import { useDailyRewards } from '@/layouts/engagement/hooks'
import { DailyRewardsCreateModal } from '@/layouts/engagement/widgets/DailyRewardsCreateModal'
import { TableZeroStateFilter } from '@/layouts/components/TableZeroStateFilter'
import { DailyRewardsQueryParams, useDailyRewardDeleteMutation } from '@/layouts/engagement/api'
import { DailyRewardRead, DailyRewardStatus, DailyRewardType } from '@/api/dashboard'
import { generatePath } from 'react-router-dom'
import { ENGAGEMENT_DAILY_REWARDS_PROGRAM_PATH, useGenerateCompanyPath } from '@/libs'
import { useCurrentUser } from '@/api/useCurrentUser'
import { ReactSortable } from 'react-sortablejs'
import { DotsGridSix } from '@/icons'
import { dashboardClient, getErrorText } from '@/api'
import { DataFlowOutline } from '@dashboard/ui/src/icons/DataFlowOutline'

export const DailyRewardsPage = () => {
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { canEdit } = useCurrentUser()
  const [orderMode, setOrderMode] = useState(false)
  const showToast = useToast()
  const openCreateModal = useModal(props => <DailyRewardsCreateModal {...props} />)
  const { mutateAsync: deleteMutateAsync } = useDailyRewardDeleteMutation(companyId, gameId)
  const filter = useSearchFilter<'q' | 'status'>()
  const hasFilter = useMemo(() => !!(filter.values.status || filter.values.q), [filter.values.status, filter.values.q])

  const { data: seasonalPrograms = [], isLoading: isSeasonLoading } = useDailyRewards({
    ...(filter.values as Partial<DailyRewardsQueryParams>),
    type: DailyRewardType.Seasonal,
  })

  const { data: basicPrograms = [], isLoading: isBasicLoading } = useDailyRewards({
    ...(filter.values as Partial<DailyRewardsQueryParams>),
    type: DailyRewardType.Basic,
  })

  const isLoading = isSeasonLoading || isBasicLoading
  const programs = [...basicPrograms, ...seasonalPrograms]
  const isZero = !isLoading && programs.length === 0

  const [orderItems, setOrderItems] = useState<DailyRewardRead[]>([])

  useEffect(() => {
    if (!isBasicLoading) {
      setOrderItems(basicPrograms || [])
    }
  }, [basicPrograms])

  const saveOrder = async () => {
    setOrderMode(false)

    const requests = []

    for (let i = 0; i < orderItems.length; i++) {
      requests.push(
        dashboardClient.v1.updateDailyReward(companyId, gameId, orderItems[i].id, {
          position: i,
        }),
      )
    }
    try {
      await Promise.all(requests)

      showToast({ message: t('saved'), severity: ToastSeverity.success })
    } catch (e) {
      showToast({ message: getErrorText(e), severity: ToastSeverity.error })
    }
  }

  const discardOrder = () => {
    setOrderItems(basicPrograms)
    setOrderMode(false)
  }

  const statusConfig = {
    [DailyRewardStatus.Active]: {
      icon: <Play />,
      variant: 'lime-secondary',
      translationKey: 'daily-rewards.status.active',
    },
    [DailyRewardStatus.Paused]: {
      icon: <Pause />,
      variant: 'orange-tertiary',
      translationKey: 'daily-rewards.status.paused',
    },
    [DailyRewardStatus.Stopped]: {
      icon: <Pause />,
      variant: 'gray-secondary',
      translationKey: 'daily-rewards.status.stopped',
    },
    [DailyRewardStatus.Scheduled]: {
      icon: <Calendar2Solid />,
      variant: 'brand-blue-tertiary',
      translationKey: 'daily-rewards.status.scheduled',
    },
    [DailyRewardStatus.Completed]: {
      icon: <Check strokeWidth={2} />,
      variant: 'gray-tertiary',
      translationKey: 'daily-rewards.status.completed',
    },
  }

  const renderZeroState = () => {
    if (isZero) {
      return (
        <TableZeroStateFilter
          page={1}
          items={programs}
          hasFilter={hasFilter}
          createClick={() => openCreateModal()}
          zeroStateMessage={t('daily-rewards.empty.title')}
          zeroStateSubMessage={t('daily-rewards.empty.message')}
          addButtonText={t('daily-rewards.program.create.button')}
          notFoundMessage={t('daily-rewards.empty.not-found')}
          clearFilterClick={filter.onReset}
        />
      )
    }
  }

  const renderStatus = (program: DailyRewardRead) => {
    const statusItem = statusConfig[program.status]

    if (!statusItem) {
      return null
    }

    return (
      <Badge variant={statusItem.variant as 'lime-secondary'}>
        {statusItem.icon}
        <span>{t(statusItem.translationKey)}</span>
      </Badge>
    )
  }

  const openConfirmDelete = useModal<{ item: DailyRewardRead }>(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: t('confirm.title'),
        subtitle: t('confirm.text', { name: props.item.name }),
      }}
      cancel={{
        label: t('confirm.no'),
      }}
      submit={{
        color: 'danger',
        callback: async () => {
          props.onClose()
          deleteMutateAsync(props.item.id)
        },
        label: t('remove'),
      }}
    />
  ))

  const renderDeleteCell = (program: DailyRewardRead) => {
    return (
      <TableCell width={60}>
        {canEdit && (
          <div
            className="text-right"
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <Popper
              popper={props => (
                <Menu {...props}>
                  <MenuItem
                    icon={Trash}
                    variant="danger"
                    onClick={() => {
                      openConfirmDelete({
                        item: program,
                      })
                      props.onClose()
                    }}
                  >
                    {t('delete')}
                  </MenuItem>
                </Menu>
              )}
            >
              <Button variant="tertiary" color="secondary">
                <DotsHorizontal />
              </Button>
            </Popper>
          </div>
        )}
      </TableCell>
    )
  }

  const renderBasicProgramsRows = () => {
    if (!orderItems.length) {
      return null
    }

    return (
      <>
        <TableRow>
          <div className="flex size-full items-center bg-fg-primary-hover p-3">
            <span className="text-caption-md font-semibold">{t('daily-rewards.table.basic')}</span>
            {orderMode ? (
              <div className="ml-auto flex items-center gap-2">
                <Button onClick={() => discardOrder()} size="xs" variant="outline">
                  {t('Discard')}
                </Button>
                <Button onClick={() => saveOrder()} size="xs">
                  {t('save-order')}
                </Button>
              </div>
            ) : (
              canEdit &&
              orderItems.length > 1 && (
                <Button onClick={() => setOrderMode(!orderMode)} size="xs" className="ml-auto" variant="outline">
                  <DataFlowOutline />
                  {t('edit-order')}
                </Button>
              )
            )}
          </div>
        </TableRow>

        <ReactSortable
          animation={200}
          delay={2}
          disabled={!canEdit || !orderMode}
          filter=".non-drag"
          list={orderItems || []}
          setList={v => {
            setOrderItems(v)
          }}
        >
          {orderItems.map(program => (
            <TableRow
              key={program.id}
              to={generatePath(ENGAGEMENT_DAILY_REWARDS_PROGRAM_PATH, {
                companyId,
                gameId,
                programId: program.id,
              })}
            >
              {orderMode && <TableCell width="5%">{canEdit && <DotsGridSix className="cursor-move" />}</TableCell>}
              <TableCell>{program.name}</TableCell>
              <TableCell width={146}>
                {program.start_at ? <DateTimeValue value={program.start_at} /> : t('daily-rewards.type.no-date')}
              </TableCell>
              <TableCell width={146}>
                {program.end_at ? <DateTimeValue value={program.end_at} /> : t('daily-rewards.type.no-date')}
              </TableCell>
              <TableCell width={120}>{renderStatus(program)}</TableCell>
              {renderDeleteCell(program)}
            </TableRow>
          ))}
        </ReactSortable>
      </>
    )
  }

  const renderSeasonalPrograms = () => {
    if (!seasonalPrograms.length) {
      return null
    }

    return (
      <>
        <TableRow>
          <div className="size-full bg-fg-primary-hover p-3">
            <span className="text-caption-md font-semibold">{t('daily-rewards.table.seasonal')}</span>
          </div>
        </TableRow>

        {seasonalPrograms.map(program => (
          <TableRow
            key={program.id}
            to={generatePath(ENGAGEMENT_DAILY_REWARDS_PROGRAM_PATH, {
              companyId,
              gameId,
              programId: program.id,
            })}
          >
            <TableCell>{program.name}</TableCell>
            <TableCell width={146}>
              {program.start_at ? <DateTimeValue value={program.start_at} /> : t('daily-rewards.type.no-date')}
            </TableCell>
            <TableCell width={146}>
              {program.end_at ? <DateTimeValue value={program.end_at} /> : t('daily-rewards.type.no-date')}
            </TableCell>
            <TableCell width={120}>{renderStatus(program)}</TableCell>
            {renderDeleteCell(program)}
          </TableRow>
        ))}
      </>
    )
  }

  const renderTable = () => {
    return (
      <div className="flex size-full flex-col">
        <div className="inline-flex justify-between gap-4">
          {(programs.length > 0 || hasFilter) && (
            <>
              <InputSearch {...filter.registerInput('q')} placeholder={t('search')} disabled={orderMode} />
            </>
          )}
        </div>

        {renderZeroState() || (
          <Table className="size-full">
            <TableRow variant="header">
              <TableCell>{t('daily-rewards.table.name')}</TableCell>
              <TableCell width={146}>{t('daily-rewards.table.start_at')}</TableCell>
              <TableCell width={146}>{t('daily-rewards.table.end_at')}</TableCell>
              <TableCell width={120}>{t('daily-rewards.table.status')}</TableCell>
              <TableCell width={60} />
            </TableRow>

            {isLoading && <TableRowSkeleton rowCount={10} columnCount={3} />}
            {!isLoading && renderBasicProgramsRows()}
            {!isLoading && renderSeasonalPrograms()}
          </Table>
        )}
      </div>
    )
  }

  return (
    <div className="flex size-full flex-col">
      <PageHeader
        sticky
        extra={
          !orderMode &&
          canEdit &&
          !isZero && (
            <Button color="primary" size="sm" onClick={() => openCreateModal()}>
              {t('daily-rewards.program.create.button')}
            </Button>
          )
        }
      >
        {t('sidebar.daily-rewards')}
      </PageHeader>

      <div className="size-full">{renderTable()}</div>
    </div>
  )
}
